import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { NoirBg } from "@/components/NoirBg";
import { Avatar } from "@/components/community/ChatList";
import { Stars, StarBadge } from "@/components/social/Stars";
import { FriendAddButton } from "@/components/social/FriendAddButton";
import { Loader2, ArrowLeft, Check, X, UserMinus, Search } from "lucide-react";

const daysLeft = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
};

const Section = ({ title, hint, children, testid }) => (
  <div className="glass-card p-5" data-testid={testid}>
    <div className="text-white font-semibold text-sm">{title}</div>
    {hint && <div className="text-[11px] text-white/45 mt-0.5 mb-3">{hint}</div>}
    <div className="space-y-2 mt-3">{children}</div>
  </div>
);

const Person = ({ person, children }) => (
  <div className="flex items-center gap-3" data-testid={`person-${person?.key}`}>
    <Link to={`/perfil/${person?.key}`} className="flex items-center gap-3 min-w-0 flex-1">
      <Avatar person={person} size={40} />
      <div className="min-w-0">
        <div className="text-sm text-white truncate flex items-center gap-1.5">
          {person?.name} <StarBadge level={person?.level} />
        </div>
        <div className="text-[11px] text-white/45 truncate">{person?.bio || "Miembro"}</div>
      </div>
    </Link>
    <div className="flex items-center gap-1.5 shrink-0">{children}</div>
  </div>
);

export default function Friends() {
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await userApi.get("/social/friends");
      setData(d);
    } catch (e) { toast.error(apiError(e)); setData({ friends: [], incoming: [], outgoing: [] }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const search = async () => {
    try {
      const { data: d } = await userApi.get("/community/members", { params: { q } });
      setMembers(d);
    } catch (e) { toast.error(apiError(e)); }
  };

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) {
    return (
      <div className="min-h-screen noir-bg theme-noir flex items-center justify-center" style={{ backgroundColor: "#050507" }}>
        <Loader2 className="w-7 h-7 animate-spin text-red-400" />
      </div>
    );
  }

  const friendKeys = new Set(data.friends.map((f) => f.key));

  return (
    <div className="min-h-screen noir-bg theme-noir" style={{ backgroundColor: "#050507" }}>
      <NoirBg />
      <header className="sticky top-0 z-30 border-b border-white/10 backdrop-blur-xl"
        style={{ background: "rgba(10,3,4,0.55)" }}>
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/muro" data-testid="friends-back"
            className="glass-btn w-9 h-9 rounded-xl flex items-center justify-center">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="font-heading font-bold text-sm text-white">Amigos</div>
          <div className="ml-auto"><Stars level={data.level} /></div>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-4 py-5 space-y-4">
        <Section testid="friends-incoming" title={`Solicitudes recibidas (${data.incoming.length})`}
          hint="Caducan solas a los 7 días si no las respondes.">
          {data.incoming.length === 0 && (
            <div className="text-xs text-white/50">No tienes solicitudes pendientes.</div>
          )}
          {data.incoming.map((r) => (
            <Person key={r.req_id} person={r.person}>
              <span className="text-[10px] text-white/40 mr-1">{daysLeft(r.expires_at)} d</span>
              <button data-testid={`accept-${r.req_id}`}
                onClick={() => act(() => userApi.post(`/social/friends/${r.req_id}/accept`), "¡Ya son amigos!")}
                className="glass-btn glass-btn-red px-3 py-1.5 rounded-xl text-xs flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> Aceptar
              </button>
              <button data-testid={`reject-${r.req_id}`}
                onClick={() => act(() => userApi.post(`/social/friends/${r.req_id}/reject`), "Solicitud rechazada")}
                className="glass-btn px-3 py-1.5 rounded-xl text-xs flex items-center gap-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </Person>
          ))}
        </Section>

        <Section testid="friends-outgoing" title={`Solicitudes enviadas (${data.outgoing.length})`}>
          {data.outgoing.length === 0 && (
            <div className="text-xs text-white/50">No has enviado solicitudes.</div>
          )}
          {data.outgoing.map((r) => (
            <Person key={r.req_id} person={r.person}>
              <span className="text-[10px] text-white/40 mr-1">{daysLeft(r.expires_at)} d</span>
              <button data-testid={`cancel-${r.req_id}`}
                onClick={() => act(() => userApi.delete(`/social/friends/request/${r.person?.key}`), "Solicitud cancelada")}
                className="glass-btn px-3 py-1.5 rounded-xl text-xs">Cancelar</button>
            </Person>
          ))}
        </Section>

        <Section testid="friends-list" title={`Mis amigos (${data.friends.length})`}>
          {data.friends.length === 0 && (
            <div className="text-xs text-white/50">Aún no tienes amigos: busca miembros abajo.</div>
          )}
          {data.friends.map((f) => (
            <Person key={f.key} person={f}>
              <button data-testid={`unfriend-${f.key}`}
                onClick={() => act(() => userApi.delete(`/social/friends/${f.key}`), "Ya no son amigos")}
                className="glass-btn px-3 py-1.5 rounded-xl text-xs flex items-center gap-1">
                <UserMinus className="w-3.5 h-3.5" /> Eliminar
              </button>
            </Person>
          ))}
        </Section>

        <Section testid="friends-search" title="Buscar miembros"
          hint="Encuentra a quien quieras y mándale una solicitud.">
          <div className="flex items-center gap-2">
            <input data-testid="friends-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Nombre o correo"
              className="glass-field flex-1 px-3 py-2 rounded-xl text-sm text-white placeholder-white/35 outline-none" />
            <button data-testid="friends-search-btn" onClick={search}
              className="glass-btn glass-btn-red w-10 h-10 rounded-xl flex items-center justify-center">
              <Search className="w-4 h-4" />
            </button>
          </div>
          {members.filter((m) => !friendKeys.has(m.key)).map((m) => (
            <Person key={m.key} person={m}>
              <FriendAddButton personKey={m.key} name={m.name} />
            </Person>
          ))}
        </Section>
      </main>
    </div>
  );
}
